import { useMemo, useState } from "react";
import { Box, Button, Collapse, LinearProgress, Typography } from "@mui/material";
import { ChevronIcon } from "@/icons";
import {
  UploadProgressCardRow,
  UploadProgressInfo,
  UploadProgressTitle,
  UploadProgressNumData,
  UploadProgressMessage,
  UploadProgressNetworkStatus,
  UploadProgressButton,
  UploadProgressSecondButton,
  UploadProgressCardActions,
  UploadProgressSecondaryInfo,
  UploadProgressSpeedDot,
  UploadProgressSpeedLabel,
  UploadProgressSpeedValue,
} from "@/components/UploadProgress/UploadProgress.elements";
import {
  UploadStateControl,
  formatPausedExpiry,
  formatRemainingTime,
} from "@/components/UploadProgress/UploadProgressLoadingCard";
import { UploadFileRowsView } from "@/components/UploadProgress/UploadFileRows";
import { CrossDeviceUploadCard } from "@/components/UploadProgress/CrossDeviceUploadCard";
import { CancelUploadDialog } from "@/components/UploadProgress/CancelUploadDialog";
import { getTelemetryDisplay } from "@/utils/uploadTelemetry";
import { getDemoState } from "./demoStates";

/**
 * Replica of the live upload card, fed by demoStates.js fixtures.
 *
 * Why a replica and not the real UploadProgressLoadingCard: the real card
 * pulls everything from module-level services (tus per-file map, telemetry
 * service, Web Locks ownership, queue snapshot). Injecting demo rows into
 * those singletons would risk leaking into real uploads. Instead this file
 * re-uses the same styled elements, the same UploadStateControl icon, the
 * same UploadFileRowsView rows and the same string helpers, so what the
 * tutorial shows is what the user will see.
 *
 * Two modes:
 *   interactive=false (default) — a static illustration: pointer events off,
 *     nothing focusable. Used on overview cards and inside articles.
 *   interactive=true — the Playground: pause/play, Select files, Take over
 *     and Cancel call back into the playground reducer via `onAction`.
 */

const noop = () => {};

const StaticWrap = ({ children, sx, ...rest }) => (
  <Box {...rest} sx={{ pointerEvents: "none", userSelect: "none", ...(sx || {}) }} aria-hidden="true">
    {children}
  </Box>
);

export const DemoUploadCard = ({
  state = "uploading",
  expandFiles = true,
  interactive = false,
  onAction = noop,
}) => {
  const demo = useMemo(() => getDemoState(state), [state]);
  const [isFilesExpanded, setIsFilesExpanded] = useState(expandFiles);
  const [isConfirmCancel, setIsConfirmCancel] = useState(false);
  const Wrap = interactive ? Box : StaticWrap;

  if (demo.remote) {
    return (
      <Wrap
        data-tour="upload-card"
        data-testid="demo-upload-card"
        data-demo-state={demo.key}
        // The real card offers admins a diagnostics drawer that calls the server
        // for this (fake) upload id — never inside the sandbox.
        sx={{ '& [data-testid^="cross-device-diagnostics-open-"]': { display: "none" } }}
      >
        <CrossDeviceUploadCard upload={demo.remote} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            data-tour="upload-take-over"
            data-testid="demo-take-over"
            onClick={() => onAction("takeOver")}
            sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}
          >
            Take over here
          </Button>
        </Box>
      </Wrap>
    );
  }

  const isPaused = demo.status === "PAUSED";
  const isCompleted = demo.status === "COMPLETED";
  const isCanceled = demo.status === "CANCELED";
  const isFinalizing = !!demo.isFinalizing;
  const telemetryDisplay = demo.telemetryStatus ? getTelemetryDisplay(demo.telemetryStatus) : null;
  const pausedAt = typeof demo.pausedAt === "function" ? demo.pausedAt() : demo.pausedAt;
  const pausedExpiryText = isPaused && pausedAt ? formatPausedExpiry(pausedAt) : null;
  const filesDone = (demo.files || []).filter((f) => f.status === "uploaded").length;
  const showActions = !isCompleted && !isCanceled;

  return (
    <Wrap data-tour="upload-card" data-testid="demo-upload-card" data-demo-state={demo.key}>
      <UploadProgressCardRow>
        <span data-tour="upload-state-control" style={{ display: "inline-flex", flexShrink: 0 }}>
          <UploadStateControl
            // Fresh usePendingClick per state so a click never leaves a spinner
            // behind when the fixture swaps underneath it.
            key={demo.key}
            status={demo.status}
            isPaused={isPaused}
            isWaiting={false}
            isFinalizing={isFinalizing || isCompleted || isCanceled}
            canInteract={interactive && !demo.needsRepick && !isCompleted && !isCanceled}
            onPause={() => onAction("pause")}
            onResume={() => onAction("resume")}
          />
        </span>

        <UploadProgressInfo>
          <UploadProgressTitle>{demo.packageName}</UploadProgressTitle>

          <Box data-tour="upload-progress">
            <LinearProgress
              variant="determinate"
              value={demo.pct}
              color={isPaused ? "warning" : isCompleted ? "success" : isCanceled ? "inherit" : "primary"}
              sx={{
                mb: "8px",
                backgroundColor: (theme) => `${theme.palette.primary.main}44`,
                ...(isCanceled ? { opacity: 0.4 } : {}),
              }}
            />

            <UploadProgressNumData>
              <span>
                {isPaused || isFinalizing || isCompleted || isCanceled
                  ? ""
                  : formatRemainingTime(demo.remainingSeconds)}
              </span>
              <span>{demo.uploadedText ?? "--"}</span>
              <span>{`${demo.pct}%`}</span>
            </UploadProgressNumData>
          </Box>

          <Box sx={{ my: 1.25, height: "1px", backgroundColor: "rgba(255,255,255,0.08)" }} />

          {!isPaused && !isFinalizing && !isCompleted && !isCanceled && (
            <UploadProgressSecondaryInfo data-tour="upload-speed">
              <UploadProgressSpeedDot />
              <UploadProgressSpeedLabel>Upload speed:</UploadProgressSpeedLabel>
              <UploadProgressSpeedValue>{demo.speedText ?? "--"}</UploadProgressSpeedValue>
            </UploadProgressSecondaryInfo>
          )}

          {demo.networkMessage && (
            <UploadProgressNetworkStatus severity={demo.networkSeverity || "warning"} role="status">
              {demo.networkMessage}
            </UploadProgressNetworkStatus>
          )}

          {telemetryDisplay?.message && (
            <UploadProgressNetworkStatus severity={telemetryDisplay.severity} role="status">
              {telemetryDisplay.message}
            </UploadProgressNetworkStatus>
          )}

          {demo.stallStatus && !isPaused && (
            <Box data-tour="upload-stall-banner" data-stall-state={demo.stallStatus}>
              <UploadProgressNetworkStatus severity="warning" role="status">
                {demo.stallStatus === "reconnecting"
                  ? "Reconnecting…"
                  : demo.stallStatus === "stalled"
                  ? "Connection stalled — retrying automatically…"
                  : "Waiting for server response…"}
              </UploadProgressNetworkStatus>
            </Box>
          )}

          {demo.statusMessage && <UploadProgressMessage>{demo.statusMessage}</UploadProgressMessage>}

          {demo.files && demo.files.length > 0 && (
            <Box
              data-tour="upload-file-rows"
              sx={{
                mt: 1.25,
                borderRadius: "10px",
                overflow: "hidden",
                background: "#2D3A4D",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <Box
                onClick={() => setIsFilesExpanded((v) => !v)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: "42px",
                  px: 1.9,
                  py: 1.1,
                  cursor: "pointer",
                  userSelect: "none",
                  background: "#35445A",
                  "&:hover": { background: "#3B4C64" },
                }}
                role="button"
                aria-expanded={isFilesExpanded}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                    color: "rgba(247,250,250,0.86)",
                  }}
                >
                  Uploading files ({filesDone}/{demo.files.length})
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Box
                  component={ChevronIcon}
                  sx={{
                    width: 9,
                    height: 9,
                    mr: 0.4,
                    color: "rgba(247,250,250,0.7)",
                    transform: isFilesExpanded ? "rotate(-180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>
              <Collapse in={isFilesExpanded} timeout={200} unmountOnExit>
                <Box
                  sx={{
                    px: 1.25,
                    pb: 1,
                    pt: 0.75,
                    background: "#2D3A4D",
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <UploadFileRowsView files={demo.files} showUploadedInKb={demo.key === "slow"} />
                </Box>
              </Collapse>
            </Box>
          )}

          {pausedExpiryText && (
            <UploadProgressMessage data-tour="upload-expires">{pausedExpiryText}</UploadProgressMessage>
          )}

          {isPaused && demo.needsRepick && (
            <UploadProgressMessage>
              This upload was interrupted — select the same files again to continue. Already uploaded chunks
              won&apos;t be re-sent.
            </UploadProgressMessage>
          )}

          {isPaused && !demo.needsRepick && (
            <UploadProgressMessage>
              Click Resume to continue from where the upload stopped — already uploaded chunks won&apos;t be
              re-sent.
            </UploadProgressMessage>
          )}

          {showActions && (
            <UploadProgressCardActions>
              {demo.needsRepick && (
                <span data-tour="upload-repick" style={{ display: "inline-flex", gap: 8 }}>
                  <UploadProgressButton onClick={() => onAction("repick")} data-testid="demo-select-folder">
                    Select folder
                  </UploadProgressButton>
                  <UploadProgressSecondButton onClick={() => onAction("repick")} data-testid="demo-select-files">
                    Select files
                  </UploadProgressSecondButton>
                </span>
              )}
              <UploadProgressSecondButton
                onClick={() => (interactive ? setIsConfirmCancel(true) : undefined)}
                data-tour="upload-cancel"
                data-testid="demo-cancel"
              >
                Cancel
              </UploadProgressSecondButton>
            </UploadProgressCardActions>
          )}
        </UploadProgressInfo>

        {interactive && (
          <CancelUploadDialog
            // Lifts the dialog above the driver.js overlay while a tour is
            // running (see tour.css) so the "press Cancel" step is doable.
            className="clipn-tour-passthrough"
            open={isConfirmCancel}
            packageName={demo.packageName}
            onClose={() => setIsConfirmCancel(false)}
            onConfirm={() => {
              setIsConfirmCancel(false);
              onAction("cancel");
            }}
          />
        )}
      </UploadProgressCardRow>
    </Wrap>
  );
};
